import React from "react";
import "./horizontal-navigation-tab.scss";

export interface HorizontalNavigationTabOption {
  label: string;
  value: string;
  disabled?: boolean;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
}

export interface HorizontalNavigationTabProps {
  tabs: HorizontalNavigationTabOption[];
  value?: string;
  onChange?: (value: string) => void;
  activeColor?: string;
  backgroundColor?: string;
  activeBackgroundColor?: string;
  rounded?: boolean;
  truncate?: boolean;
  className?: string;
}

export const HorizontalNavigationTab: React.FC<
  HorizontalNavigationTabProps
> = ({ tabs, value, onChange, activeColor, backgroundColor, activeBackgroundColor, rounded = false, truncate = false, className = "" }) => {
  const handleClick = (tab: HorizontalNavigationTabOption) => {
    if (!tab.disabled) {
      onChange?.(tab.value);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    tab: HorizontalNavigationTabOption,
    index: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick(tab);
      return;
    }

    const enabledTabs = tabs
      .map((t, i) => ({ tab: t, index: i }))
      .filter((t) => !t.tab.disabled);

    const currentPos = enabledTabs.findIndex((t) => t.index === index);
    let nextPos = -1;

    if (event.key === "ArrowRight") {
      nextPos = (currentPos + 1) % enabledTabs.length;
    } else if (event.key === "ArrowLeft") {
      nextPos = (currentPos - 1 + enabledTabs.length) % enabledTabs.length;
    } else if (event.key === "Home") {
      nextPos = 0;
    } else if (event.key === "End") {
      nextPos = enabledTabs.length - 1;
    }

    if (nextPos >= 0) {
      const nextTab = enabledTabs[nextPos];
      if (!nextTab) return;
      event.preventDefault();
      const el = (event.currentTarget as HTMLElement)
        .parentElement?.children[nextTab.index] as HTMLElement | undefined;
      el?.focus();
      onChange?.(nextTab.tab.value);
    }
  };

  const containerStyle: React.CSSProperties = {
    ...(activeColor ? { "--horizontal-nav-active-color": activeColor } : {}),
    ...(backgroundColor ? { "--horizontal-nav-bg-color": backgroundColor } : {}),
    ...(activeBackgroundColor ? { "--horizontal-nav-active-bg-color": activeBackgroundColor } : {}),
  } as React.CSSProperties;

  return (
    <div
      className={`horizontal-navigation-tab ${truncate ? "horizontal-navigation-tab--truncate" : ""} ${className}`}
      style={containerStyle}
      role="tablist"
    >
      {tabs.map((tab, index) => {
        const isActive = value === tab.value;
        const hasExtraContent = tab.leftContent || tab.rightContent;

        const itemClasses = [
          "horizontal-navigation-tab__item",
          isActive && "horizontal-navigation-tab__item--active",
          tab.disabled && "horizontal-navigation-tab__item--disabled",
          rounded && "horizontal-navigation-tab__item--rounded",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <button
            key={tab.value}
            className={itemClasses}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => handleClick(tab)}
            onKeyDown={(e) => handleKeyDown(e, tab, index)}
            type="button"
          >
            {hasExtraContent ? (
              <span className="horizontal-navigation-tab__content">
                {tab.leftContent && (
                  <span className="horizontal-navigation-tab__left">
                    {tab.leftContent}
                  </span>
                )}
                <span
                  className={`horizontal-navigation-tab__label ${truncate ? "horizontal-navigation-tab__label--truncate" : ""}`}
                  title={truncate ? tab.label : undefined}
                >
                  {tab.label}
                </span>
                {tab.rightContent && (
                  <span className="horizontal-navigation-tab__right">
                    {tab.rightContent}
                  </span>
                )}
              </span>
            ) : (
              <span
                className={`horizontal-navigation-tab__label ${truncate ? "horizontal-navigation-tab__label--truncate" : ""}`}
                title={truncate ? tab.label : undefined}
              >
                {tab.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
